import React, { Component } from 'react';
import { observer } from 'mobx-react';
import { observable, action } from 'mobx';
import moment from 'moment';
import {
    Milestone, MilestoneStore,
    PlanningTicket, PlanningTicketStore,
} from 'store/planning';
import { ProjectStore } from 'store/Project';
import { UserStore } from 'store/User';
import { TicketCard } from './TicketCard';
import { MilestoneForm } from './MilestoneForm';
import { TicketForm } from './TicketForm';

interface Props {
    milestoneStore: MilestoneStore;
    ticketStore: PlanningTicketStore;
    projectStore: ProjectStore;
    userStore: UserStore;
    visibleProjects: any[];
}

@observer
export class SprintPlanningTab extends Component<Props> {
    @observable expandedMilestones = new Set<number>();
    @observable milestoneFormOpen = false;
    @observable editingMilestone: Milestone | null = null;
    @observable ticketFormOpen = false;
    @observable editingTicket: PlanningTicket | null = null;
    @observable ticketFormMilestone: Milestone | null = null;

    @action
    toggleMilestone = (id: number) => {
        if (this.expandedMilestones.has(id)) {
            this.expandedMilestones.delete(id);
        } else {
            this.expandedMilestones.add(id);
        }
    };

    @action
    openMilestoneForm = (milestone?: Milestone) => {
        this.editingMilestone = milestone || null;
        this.milestoneFormOpen = true;
    };

    @action
    closeMilestoneForm = () => {
        this.milestoneFormOpen = false;
        this.editingMilestone = null;
    };

    @action
    openTicketForm = (milestone: Milestone | null, ticket?: PlanningTicket) => {
        this.ticketFormMilestone = milestone;
        this.editingTicket = ticket || null;
        this.ticketFormOpen = true;
    };

    @action
    closeTicketForm = () => {
        this.ticketFormOpen = false;
        this.editingTicket = null;
        this.ticketFormMilestone = null;
    };

    @action
    handleAddTicketTopLevel = () => {
        // If there are milestones, open ticket form with the first milestone.
        // If no milestones exist, open the milestone form instead.
        const { milestoneStore } = this.props;
        if (milestoneStore.models.length === 0) {
            this.openMilestoneForm();
        } else {
            const first = milestoneStore.models[0] as Milestone;
            this.openTicketForm(first);
        }
    };

    getMilestoneTickets = (milestoneId: number): PlanningTicket[] => {
        return this.props.ticketStore.models.filter((t: any) => t.milestone?.id === milestoneId);
    };

    handleStatusChange = async (ticket: PlanningTicket, status: string) => {
        try {
            const api = (this.props.ticketStore as any).api;
            await api.patch(`planning_ticket/${ticket.id}/`, { status });
            await this.props.ticketStore.fetch({ with: ['milestone', 'assignee'] });
        } catch (e) {
            console.error('Failed to update ticket status:', e);
        }
    };

    formatDate = (date: any): string => {
        if (!date) return '';
        if (date.format) return date.format('MMM D, YYYY');
        try { return moment(date).format('MMM D, YYYY'); } catch { return String(date); }
    };

    getProjectLabel = (project: any): string => {
        if (!project) return '';
        const company = project.company;
        const companyName = company ? (company.name || '') : '';
        const projectName = project.name || project.fullName || '';
        return companyName ? `${companyName} — ${projectName}` : projectName;
    };

    renderMilestone = (milestone: Milestone) => {
        const id = milestone.id as number;
        const isExpanded = this.expandedMilestones.has(id);
        const tickets = this.getMilestoneTickets(id);
        const project = milestone.project as any;
        const projectLabel = this.getProjectLabel(project);
        const totalHours = tickets.reduce((sum, t) => sum + (t.estimatedHours || 0), 0);

        return (
            <div className="sp-milestone-card" key={id} data-test-milestone-card={id}>
                <div
                    className="sp-milestone-header"
                    onClick={() => this.toggleMilestone(id)}
                    data-test-milestone-toggle={id}
                >
                    <div className="sp-milestone-info">
                        <div>
                            <div className="sp-milestone-name">{milestone.name}</div>
                            <div className="sp-milestone-meta">
                                <span className={`sp-badge sp-badge-${milestone.status}`}>{milestone.status}</span>
                                <span className="sp-milestone-type">{milestone.type}</span>
                                {projectLabel && <span className="sp-milestone-project">{projectLabel}</span>}
                                {milestone.startDate && <span>Start: {this.formatDate(milestone.startDate)}</span>}
                                {milestone.deadline && <span>Deadline: {this.formatDate(milestone.deadline)}</span>}
                                <span>{tickets.length} tickets · {totalHours}h</span>
                            </div>
                        </div>
                    </div>
                    <div className="sp-milestone-actions">
                        <button
                            className="sp-btn sp-btn-sm"
                            onClick={(e) => { e.stopPropagation(); this.openTicketForm(milestone); }}
                            data-test-add-ticket={id}
                        >
                            + Ticket
                        </button>
                        <button
                            className="sp-btn-icon"
                            onClick={(e) => { e.stopPropagation(); this.openMilestoneForm(milestone); }}
                            data-test-edit-milestone={id}
                            title="Edit milestone"
                        >
                            ✎
                        </button>
                        <span style={{ color: 'var(--sp-text-secondary)', fontSize: '0.8rem' }}>
                            {isExpanded ? '▼' : '▶'}
                        </span>
                    </div>
                </div>
                {isExpanded && (
                    <div className="sp-tickets-section">
                        <div className="sp-tickets-header">
                            <span className="sp-tickets-title">Tickets ({tickets.length})</span>
                        </div>
                        {tickets.length === 0 ? (
                            <div className="sp-empty-tickets">No tickets yet. Click "+ Ticket" to add one.</div>
                        ) : (
                            <div className="sp-tickets-grid">
                                {tickets.map(ticket => (
                                    <TicketCard
                                        key={ticket.id}
                                        ticket={ticket}
                                        onEdit={(t) => this.openTicketForm(milestone, t)}
                                        onStatusChange={this.handleStatusChange}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    };

    render() {
        const { milestoneStore, ticketStore, projectStore, userStore, visibleProjects } = this.props;

        return (
            <div className="sp-milestone-list" data-test-sprint-planning-tab>
                <div className="sp-header-actions" style={{ marginBottom: '1rem', justifyContent: 'flex-end' }}>
                    <button
                        className="sp-btn"
                        onClick={this.handleAddTicketTopLevel}
                        data-test-add-ticket-top
                    >
                        + New Ticket
                    </button>
                    <button
                        className="sp-btn sp-btn-primary"
                        onClick={() => this.openMilestoneForm()}
                        data-test-add-milestone
                    >
                        + New Milestone
                    </button>
                </div>

                {milestoneStore.models.length === 0 ? (
                    <div className="sp-empty-state">
                        <h3>No Milestones Yet</h3>
                        <p>Create a milestone to start planning your sprint.</p>
                    </div>
                ) : (
                    milestoneStore.models.map(m => this.renderMilestone(m as Milestone))
                )}

                <MilestoneForm
                    open={this.milestoneFormOpen}
                    milestone={this.editingMilestone}
                    milestoneStore={milestoneStore}
                    projectStore={projectStore}
                    visibleProjects={visibleProjects}
                    onClose={this.closeMilestoneForm}
                    onSaved={this.closeMilestoneForm}
                />
                <TicketForm
                    open={this.ticketFormOpen}
                    ticket={this.editingTicket}
                    milestone={this.ticketFormMilestone}
                    ticketStore={ticketStore}
                    userStore={userStore}
                    onClose={this.closeTicketForm}
                    onSaved={this.closeTicketForm}
                />
            </div>
        );
    }
}

export default SprintPlanningTab;
